import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import type DestinationRoute from 'lumen/routes/destination';
import {
  BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
  MI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
} from 'lumen/utils/constants/general-constants';
import {
  TRIAL_INDICATION_LOCALSTORAGE,
  TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
  TRIAL_INDICATION_LOCALSTORAGE_KEY,
  TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
} from 'lumen/utils/constants/trial-indication-constants';
import createAccountMenu from 'lumen/utils/mount-react/create-account-menu';
import createAccountSelector from 'lumen/utils/mount-react/create-account-selector';
import type { ModelFrom } from 'lumen/utils/routes';
import type {
  AccountMenuProps,
  AccountSelectorProps,
} from 'shared-react/lumen';

export default class DestinationController extends Controller {
  declare model: ModelFrom<DestinationRoute>;

  @service declare access: Services['access'];

  @service declare ajax: Services['ajax'];
  @service declare destination: Services['destination'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: RouterServiceWithLegacyTransitionTo;

  active = false;

  get accountMenuOptions(): Omit<AccountMenuProps, 'kind'> {
    return createAccountMenu(this);
  }

  get accountSelectorOptions(): AccountSelectorProps | undefined {
    const destinationSubscription = this.destination.destinationSubscription;

    if (!destinationSubscription?.id) {
      return undefined;
    }

    return createAccountSelector({
      current: {
        type: 'destination',
        id: destinationSubscription.id,
        name: destinationSubscription.name,
        plans: [],
        products: [],
      },
      ajax: this.ajax,
      router: this.router,
      access: this.access,
      errorTracking: this.errorTracking,
      isDemoMode: this.isDemoMode,
    });
  }

  get isDemoMode(): boolean {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSettingsButtonActive() {
    return this.router.currentRouteName!.includes('settings');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get settingsOptions() {
    const res = [
      {
        label: this.intl.t('menu.my_account'),
        action: 'redirectToMyAccount',
        icon: 'circle-user' as const,
      },
      {
        label: this.intl.t('menu.help'),
        action: 'openHelpCenter',
        icon: 'circle-question-mark' as const,
      },
      {
        label: this.intl.t('menu.refer_a_friend'),
        action: 'redirectReferAFriend',
        icon: 'refer-friend' as const,
      },
      {
        label: this.intl.t('menu.logout'),
        action: 'logout',
        icon: 'logout' as const,
      },
    ];

    return res;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEmptyState() {
    const isSettingsPage = this.router.currentRouteName!.includes('.settings.');
    return this.access.isContextHiddenForStaffUsers && !isSettingsPage;
  }

  @action dummy(option: Option): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.send(option['action']);
  }

  @action logout(): void {
    // Segment stores personal info in localstorage
    // Remove it before logging out
    this.localStorage.removeItem('ajs_user_traits');
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE);
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(BI_UPSELL_CONTACT_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY);

    // Clear firebase auth data
    indexedDB.deleteDatabase('firebaseLocalStorageDb');

    // this.featureToggle.close();
    this.ajax
      .post('/logout/')
      .then(() => {
        window.location.href = '/login';
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }

  @action openHelpCenter(): void {
    this.intercom.redirectToIntercomHelp();
  }

  @action redirectReferAFriend(): void {
    const extraData = [
      {
        name: 'referrer_first_name',
        value: this.hotelAndMarketService.user?.firstName,
      },
      {
        name: 'referrer_last_name',
        value: this.hotelAndMarketService.user?.lastName,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_property_name',
        value: this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name,
      },
    ];

    // Refer a friend menu item is not visible for resellers, so no extra checks required here
    const baseUrl = `https://www.${DOMAIN_NAME}/lp/refer-a-friend`;

    // form a string containing all necessary URL parameters
    let urlParams = '?';

    extraData.forEach((param, index) => {
      urlParams += `${param.name}=${encodeURIComponent(param.value)}`;

      if (index !== extraData.length - 1) {
        urlParams += '&';
      }
    });

    openUrl(`${baseUrl}${urlParams}`);
  }

  @action redirectToMyAccount(): void {
    this.router.transitionTo('destination.settings.personal');
  }

  @action redirectToSettings(): void {
    this.router.transitionTo('destination.settings');
  }

  @action toggleActive(): void {
    this.active = !this.active;
  }
}
